import {onDutyFree} from '@festi/common/constants/channels'
import settings from '@festi/common/constants/settings'
import sanitizeHtml from 'sanitize-html'

export const defaultMetaTitle = onDutyFree
  ? 'ELKO í Flugstöð, Keflavíkurflugvelli'
  : 'ELKO - Mesta úrval landsins af raftækjum.'

const baseUrl = onDutyFree ? settings.dutyfreeUrl : settings.baseUrl

/** Add title suffix appended to all titles for each store */
const defaultMetaTitleSuffix = onDutyFree ? ' | ELKO Flugstöð' : ' | ELKO'

const defaultMetaDescription = onDutyFree
  ? 'ELKO í Flugstöð, Keflavíkurflugvelli. Þú getur pantað vörur á síðunni okkar og sótt á leið úr landi, í brottfaraverslun.'
  : 'ELKO - Mesta úrval landsins af raftækjum. Sendum um allt land eða í næstu verslun. Það er einfalt og þægilegt að versla á elko.is.'

/** Add description suffix specific to duty free store */
const defaultMetaDescriptionSuffix = onDutyFree ? ' | ELKO Flugstöð' : ''

const defaultSiteName = onDutyFree ? 'ELKO Flugstöð' : 'ELKO'

const defaultImages = [
  {
    url: `${baseUrl}/logo/elko-white-on-blue-1200x630.jpg`,
    alt: 'ELKO',
    width: 1200,
    height: 630,
  },
]

/**
 * Appends default title suffix to title if it doesn't already contain 'ELKO'
 */
function addTitleSuffix(title: string): string {
  return title.includes('ELKO') ? title : title + defaultMetaTitleSuffix
}

/**
 * Returns the meta title for a category or the default meta title
 */
export function getMetaTitle({
  category,
}: {
  category: {seoTitle?: string; name?: string} | undefined
}) {
  if (!category) return defaultMetaTitle
  const baseTitle = (
    category.seoTitle ||
    category.name ||
    defaultMetaTitle
  ).trim()
  return baseTitle
}

/**
 * Returns a meta description for the relevant page
 * Search page, Category page, Product page, or fallback if none
 */
export function getMetaDescription(options: {
  searchTermLower?: string
  category?: {
    name?: string
    seoDescription?: string
    descriptionPlaintext?: string
  }
  shortDescription?: string
  fallbackPrefix?: string
}): string {
  // Search page
  if (options.searchTermLower !== undefined) {
    return options.searchTermLower
      ? `„${options.searchTermLower}“ hjá ${defaultMetaDescription}`
      : `Vöruleit hjá ${defaultMetaDescription}`
  }

  // Category page
  if (options.category) {
    const {name, seoDescription, descriptionPlaintext} = options.category
    let desc = seoDescription || descriptionPlaintext
    if (!desc) {
      desc = `${name} hjá ${defaultMetaDescription}`
      return desc
    }
    return desc + defaultMetaDescriptionSuffix
  }

  // Product page (short description)
  if (options.shortDescription) {
    return options.shortDescription + defaultMetaDescriptionSuffix
  }

  // Fallback with prefix or default
  return options.fallbackPrefix
    ? options.fallbackPrefix + defaultMetaDescription
    : defaultMetaDescription
}

export interface NextSeoInput {
  seoTitle?: string
  seoDescription?: string
  seoCanonical?: string
  seoImages?: Array<{url: string; alt?: string}>
  seoType?: string
  seoNoIndex?: boolean
  seoNoFollow?: boolean
}

/**
 * Extracts SEO fields from a DatoCMS Page_page object for use with NextSeo in WebPage.
 * @param page DatoCMS Page_page object
 * @returns NextSeoInput fields for NextSeo in WebPage
 */
export function extractSeoFromDatoPage(page: {seo?: any[]}): NextSeoInput {
  const metaTags = [...(page.seo || [])]
  let seoTitle: string | undefined
  let seoDescription: string | undefined
  let seoImages: Array<{
    url: string
    alt?: string
    width?: number
    height?: number
  }> = []

  const imageMap: Record<
    string,
    {url: string; alt?: string; width?: number; height?: number}
  > = {}

  for (const tag of metaTags) {
    if (tag.tag === 'title' && tag.content) {
      seoTitle = tag.content
    }
    if (tag.tag === 'meta' && tag.attributes) {
      if (tag.attributes.name === 'description' && tag.attributes.content) {
        seoDescription = tag.attributes.content
      }
      if (tag.attributes.property === 'og:image' && tag.attributes.content) {
        imageMap[tag.attributes.content] = {url: tag.attributes.content}
      }
      if (tag.attributes.property === 'og:image:alt' && tag.attributes.content) {
        const firstImage = Object.keys(imageMap)[0]
        if (firstImage) imageMap[firstImage].alt = tag.attributes.content
      }
      if (tag.attributes.property === 'og:image:width' && tag.attributes.content) {
        const firstImage = Object.keys(imageMap)[0]
        if (firstImage) imageMap[firstImage].width = Number(tag.attributes.content)
      }
      if (tag.attributes.property === 'og:image:height' && tag.attributes.content) {
        const firstImage = Object.keys(imageMap)[0]
        if (firstImage) imageMap[firstImage].height = Number(tag.attributes.content)
      }
    }
  }

  seoImages = Object.values(imageMap)

  return {
    seoTitle,
    seoDescription,
    seoImages: seoImages.length > 0 ? seoImages : undefined,
  }
}

/**
 * Builds the props object for NextSeo, applying defaults and structure.
 */
export function getNextSeoProps({
  seoTitle,
  seoDescription,
  seoCanonical,
  seoImages,
  seoType,
  seoNoIndex = false,
  seoNoFollow = false,
}: NextSeoInput) {
  const title = seoTitle ? addTitleSuffix(seoTitle) : defaultMetaTitle
  const description = seoDescription
    ? sanitizeHtml(seoDescription.trim(), {allowedTags: []})
    : defaultMetaDescription
  const noIndex = seoNoIndex === true ? {noindex: true} : {}
  const noFollow = seoNoFollow === true ? {nofollow: true} : {}

  if (settings.algoliaEnv === 'stage' || settings.algoliaEnv === 'dev') {
    noIndex.noindex = true
    noFollow.nofollow = true
  }

  return {
    title,
    description,
    canonical: seoCanonical,
    ...noIndex,
    ...noFollow,
    openGraph: {
      title,
      description,
      images: seoImages || defaultImages,
      siteName: defaultSiteName,
      type: seoType || undefined,
    },
  }
}
